// Feature: docs/features/deterministic-rules-management.md
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  VStack,
  Button,
  useToast,
  Spinner,
  Text,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import HeaderMenus from '../HeaderMenus';
import RulesTable from './RulesTable';
import RuleModal from './RuleModal';
import IonmBaselineConfig from './IonmBaselineConfig';
import IonmGeneralRulesConfig from './IonmGeneralRulesConfig';
import AscFacilitiesConfig from './AscFacilitiesConfig';
import ClaimRulesConfig from './ClaimRulesConfig';
import DynamicRulesConfig from './DynamicRulesConfig';
import RuleExecutionStats from './RuleExecutionStats';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';

const CptRulesManager = () => {
  const [rules, setRules] = useState([]);
  const [actions, setActions] = useState([]);
  const [standardPayers, setStandardPayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const fetchRules = async () => {
    if (!accessToken) return;
    try {
      const response = await callApi('/api/v1/deterministic-rules', accessToken);
      
      if (!response.ok) throw new Error('Failed to fetch rules');
      
      const data = await response.json();
      setRules(data);
    } catch (error) {
      toast({
        title: 'Error fetching rules',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchActions = async () => {
    if (!accessToken) return;
    try {
      const response = await callApi('/api/v1/deterministic-rules/actions', accessToken);
      
      if (!response.ok) throw new Error('Failed to fetch actions');
      
      const data = await response.json();
      setActions(data);
    } catch (error) {
      toast({
        title: 'Error fetching actions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchStandardPayers = async () => {
    if (!accessToken) return;
    try {
      const response = await callApi('/api/v1/deterministic-rules/payers', accessToken);
      
      if (!response.ok) throw new Error('Failed to fetch standard payers');
      
      const data = await response.json();
      // If the backend returns an empty array (e.g., for non-155 orgs), 
      // we set it to null to signal the RuleModal to use the fallback behavior
      if (data && data.length > 0) {
        // Sort the standard payers alphabetically
        const sortedData = [...data].sort((a, b) => a.localeCompare(b));
        setStandardPayers(sortedData);
      } else {
        setStandardPayers(null);
      }
    } catch (error) {
      toast({
        title: 'Error fetching standard payers',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (accessToken) {
        setIsLoading(true);
        await Promise.all([fetchRules(), fetchActions(), fetchStandardPayers()]);
        setIsLoading(false);
      }
    };
    loadData();
  }, [accessToken]);

  const handleCreateRule = () => {
    setIsModalOpen(true);
  };

  const handleSaveRule = async (ruleData) => {
    try {
      const response = await callApi('/api/v1/deterministic-rules', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ruleData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save rule');
      }

      toast({
        title: 'Rule created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setIsModalOpen(false);
      fetchRules();
    } catch (error) {
      toast({
        title: 'Error saving rule',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleStopRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to stop this rule? It will no longer apply to new claims.')) {
      return;
    }

    try {
      const response = await callApi(`/api/v1/deterministic-rules/${ruleId}`, accessToken, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to stop rule');
      }

      toast({
        title: 'Rule stopped successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchRules();
    } catch (error) {
      toast({
        title: 'Error stopping rule',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleExportToCSV = () => {
    if (rules.length === 0) {
      toast({
        title: 'No rules to export',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const headers = ['ID', 'Rule Type', 'Target Name', 'Action', 'Effective Start Date'];
    
    const escapeCSV = (str) => {
      if (str === null || str === undefined) return '';
      const stringValue = String(str);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const rows = rules.map(rule => [
      escapeCSV(rule.id),
      escapeCSV(rule.rule_type),
      escapeCSV(rule.target_name),
      escapeCSV(rule.action),
      escapeCSV(rule.effective_start_date)
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'CPT_Rules_Export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Derived lists for dropdowns in the modal
  // We now use the standardPayers list from the backend for payers, but keep uniqueCarriers derived from existing rules
  const uniqueCarriers = [...new Set(rules.filter(r => r.rule_type === 'carrier').map(r => r.target_name))].filter(Boolean).sort();
  
  // Grouping rules for the dashboard view to match execution sequence
  // We filter out rules that have JSON conditions, as they belong in the Advanced Rules tab
  const legacyRules = rules.filter(r => !r.conditions);
  const dynamicRules = rules.filter(r => r.conditions);
  
  // Phase 1: All Pre
  const allPreRules = legacyRules.filter(r => r.rule_type === 'all-pre');

  // Phase 2: Payers
  const rulesByPayer = {};
  legacyRules.filter(r => r.rule_type === 'payer').forEach(r => {
    const name = r.target_name || 'Unknown';
    if (!rulesByPayer[name]) rulesByPayer[name] = [];
    rulesByPayer[name].push(r);
  });

  // Phase 3: Carriers
  const rulesByCarrier = {};
  legacyRules.filter(r => r.rule_type === 'carrier').forEach(r => {
    const name = r.target_name || 'Unknown';
    if (!rulesByCarrier[name]) rulesByCarrier[name] = [];
    rulesByCarrier[name].push(r);
  });

  // Phase 4: All Post (Modifiers/Pairs)
  const allPostRules = legacyRules.filter(r => r.rule_type === 'all-post');

  return (
    <Box bg="gray.50" minH="100vh">
      <HeaderMenus />
      <Box p={5}>
        <VStack spacing={6} align="stretch">
          <Flex justify="space-between" align="center">
            <Heading size="lg" color="blue.800">CPT Rules Management</Heading>
          </Flex>

          <Tabs variant="enclosed" colorScheme="blue">
            <TabList>
              <Tab>Deterministic Rules</Tab>
              <Tab>Advanced Rules</Tab>
              <Tab>Execution Stats</Tab>
              <Tab>IONM Baselines</Tab>
              <Tab>ASC Facilities</Tab>
              <Tab>General Rules</Tab>
              <Tab>Claim Rules</Tab>
            </TabList>

            <TabPanels>
              {/* Tab 1: Deterministic Rules */}
              <TabPanel px={0}>
                <Flex justify="flex-end" mb={4}>
                  <HStack spacing={4}>
                    <Button 
                      leftIcon={<DownloadIcon />} 
                      colorScheme="green" 
                      variant="outline"
                      onClick={handleExportToCSV}
                      isDisabled={rules.length === 0}
                    >
                      Export All Rules
                    </Button>
                    <Button 
                      leftIcon={<AddIcon />} 
                      colorScheme="blue" 
                      onClick={handleCreateRule}
                    >
                      Create Rule
                    </Button>
                  </HStack>
                </Flex>

                <Box bg="white" borderRadius="md" boxShadow="sm" borderWidth="1px" p={4}>
                  {isLoading ? (
                    <Flex justify="center" align="center" p={10}>
                      <Spinner size="xl" color="blue.500" />
                    </Flex>
                  ) : rules.length === 0 ? (
                    <Text textAlign="center" color="gray.500" p={5}>
                      No active rules found. Create one to get started.
                    </Text>
                  ) : (
                    <VStack spacing={8} align="stretch">
                      
                      {/* Phase 1: All Pre Section */}
                      <Box>
                        <Heading size="md" mb={2} color="gray.700">
                          Phase 1: Global Pre-Rules <Badge ml={2} colorScheme="purple">{allPreRules.length}</Badge>
                        </Heading>
                        <Text fontSize="sm" color="gray.500" mb={4}>Runs first. Applies to all claims before any payer logic.</Text>
                        {allPreRules.length > 0 ? (
                          <RulesTable rules={allPreRules} onStop={handleStopRule} />
                        ) : (
                          <Text color="gray.500" fontSize="sm">No global pre-rules configured.</Text>
                        )}
                      </Box>

                      <Divider />

                      {/* Phase 2: Payers Section */}
                      <Box>
                        <Heading size="md" mb={2} color="gray.700">
                          Phase 2: Payer-Specific Rules
                        </Heading>
                        <Text fontSize="sm" color="gray.500" mb={4}>Runs second. Applies structural changes based on the Payer.</Text>
                        {Object.keys(rulesByPayer).length > 0 ? (
                          <Accordion allowMultiple>
                            {Object.keys(rulesByPayer).sort().map(payer => (
                              <AccordionItem key={payer}>
                                <h2>
                                  <AccordionButton _expanded={{ bg: 'blue.50', color: 'blue.700' }}>
                                    <Box flex="1" textAlign="left" fontWeight="medium">
                                      {payer}
                                    </Box>
                                    <Badge colorScheme="blue" mr={3}>{rulesByPayer[payer].length} Rules</Badge>
                                    <AccordionIcon />
                                  </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4}>
                                  <RulesTable rules={rulesByPayer[payer]} onStop={handleStopRule} />
                                </AccordionPanel>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        ) : (
                          <Text color="gray.500" fontSize="sm">No payer-specific rules configured.</Text>
                        )}
                      </Box>

                      <Divider />

                      {/* Phase 3: Carriers Section */}
                      <Box>
                        <Heading size="md" mb={2} color="gray.700">
                          Phase 3: Carrier-Specific Rules
                        </Heading>
                        <Text fontSize="sm" color="gray.500" mb={4}>Runs third. Applies structural changes based on the Carrier.</Text>
                        {Object.keys(rulesByCarrier).length > 0 ? (
                          <Accordion allowMultiple>
                            {Object.keys(rulesByCarrier).sort().map(carrier => (
                              <AccordionItem key={carrier}>
                                <h2>
                                  <AccordionButton _expanded={{ bg: 'cyan.50', color: 'cyan.700' }}>
                                    <Box flex="1" textAlign="left" fontWeight="medium">
                                      {carrier}
                                    </Box>
                                    <Badge colorScheme="cyan" mr={3}>{rulesByCarrier[carrier].length} Rules</Badge>
                                    <AccordionIcon />
                                  </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4}>
                                  <RulesTable rules={rulesByCarrier[carrier]} onStop={handleStopRule} />
                                </AccordionPanel>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        ) : (
                          <Text color="gray.500" fontSize="sm">No carrier-specific rules configured.</Text>
                        )}
                      </Box>

                      <Divider />

                      {/* Phase 4: All Post Section */}
                      <Box>
                        <Heading size="md" mb={2} color="gray.700">
                          Phase 4: Post-Rules (Modifiers & Pairs) <Badge ml={2} colorScheme="green">{allPostRules.length}</Badge>
                        </Heading>
                        <Text fontSize="sm" color="gray.500" mb={4}>Runs last. Used for NCCI modifiers and final checks across all or specific payers.</Text>
                        {allPostRules.length > 0 ? (
                          <RulesTable rules={allPostRules} onStop={handleStopRule} />
                        ) : (
                          <Text color="gray.500" fontSize="sm">No post-rules configured.</Text>
                        )}
                      </Box>

                    </VStack>
                  )}
                </Box>
              </TabPanel>

              {/* Tab 2: Advanced Rules */}
              <TabPanel px={0}>
                <DynamicRulesConfig 
                  rules={dynamicRules} 
                  actions={actions} 
                  standardPayers={standardPayers} 
                  uniqueCarriers={uniqueCarriers}
                  onSaveRule={handleSaveRule}
                  onStopRule={handleStopRule}
                  isLoading={isLoading}
                />
              </TabPanel>

              {/* Tab 3: Execution Stats */}
              <TabPanel px={0}>
                <RuleExecutionStats />
              </TabPanel>

              {/* Tab 4: IONM Baselines */}
              <TabPanel px={0}>
                <IonmBaselineConfig />
              </TabPanel>

              {/* Tab 5: ASC Facilities */}
              <TabPanel px={0}>
                <AscFacilitiesConfig />
              </TabPanel>

              {/* Tab 6: General Rules */}
              <TabPanel px={0}>
                <IonmGeneralRulesConfig />
              </TabPanel>

              {/* Tab 7: Claim Rules */}
              <TabPanel px={0}>
                <ClaimRulesConfig />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Box>

      <RuleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveRule}
        availableActions={actions}
        existingPayers={standardPayers}
        existingCarriers={uniqueCarriers}
      />
    </Box>
  );
};

export default CptRulesManager;
